/**
 * Centralized humanizer for enum-style status / type strings coming from the
 * backend (e.g. `IN_PROGRESS`, `BOOKING_ACCEPTED`, `OPS_ESCALATION`).
 *
 * Use `formatStatus(value)` anywhere we render a raw enum to a user. The
 * function is intentionally forgiving — values that are already
 * human-readable (mixed case, spaces, or short strings) are returned with
 * sentence-case applied so we never accidentally double-prettify.
 *
 * Add explicit overrides for any code whose default prettification is wrong
 * (e.g. `OTP_OVERRIDE_REQUEST` reads better as "OTP override request" with a
 * preserved acronym).
 */

/** Explicit overrides win over the generic prettifier. */
const STATUS_OVERRIDES = Object.freeze({
  // Booking lifecycle
  PENDING: "Pending",
  ACCEPTED: "Accepted",
  REJECTED: "Rejected",
  CONFIRMED: "Confirmed",
  RESCHEDULED: "Rescheduled",
  RESCHEDULE_REQUESTED: "Reschedule requested",
  AWAITING_CAREGIVER: "Awaiting caregiver",
  WAITING_CAREGIVER: "Waiting for caregiver",
  SEARCHING_REPLACEMENT: "Searching for replacement",
  SLA_ESCALATED: "Care team assisting",
  IN_PROGRESS: "In progress",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  CANCELED: "Cancelled",
  OPS_ESCALATION: "Ops escalation",
  ESCALATION: "Escalation",
  COMPLAINT: "Complaint",
  DISPUTE: "Dispute",
  ESCALATED_TO_OPS: "Escalated to Ops",
  URGENT: "Urgent",
  PAYMENT_PENDING: "Payment pending",
  AWAITING_OTP: "Awaiting OTP",

  // Task / sub-status
  TASK_PENDING: "Pending",
  TASK_IN_PROGRESS: "In progress",
  TASK_COMPLETED: "Completed",

  // Payment
  PAID: "Paid",
  UNPAID: "Unpaid",
  FAILED: "Failed",
  REFUNDED: "Refunded",
  PARTIALLY_REFUNDED: "Partially refunded",
  CAPTURED: "Captured",
  SUCCESS: "Success",
  PAYMENT_DONE: "Payment done",

  // Notification / edge-event types
  BOOKING_REQUEST: "New booking request",
  BOOKING_ACCEPTED: "Booking accepted",
  BOOKING_REJECTED: "Booking rejected",
  BOOKING_CANCELLED: "Booking cancelled",
  BOOKING_COMPLETED: "Booking completed",
  TASK_STARTED: "Task started",
  REMINDER_24H: "Reminder · 24 hours before",
  REMINDER_1H: "Reminder · 1 hour before",
  REMINDER_START: "Reminder · start time",
  CUSTOMER_CANCEL_REQUEST: "Customer cancel request",
  CAREGIVER_CANCEL_REQUEST: "Caregiver cancel request",
  LATE_ARRIVAL: "Late arrival",
  VISIT_OVERRUN: "Visit overrun",
  OTP_OVERRIDE_REQUEST: "OTP override request",
  EDGE_CUSTOMER_CANCEL_REQUEST: "Edge · cancel request",
  EDGE_CAREGIVER_CANCEL_REQUEST: "Edge · cancel request",
  EDGE_LATE_ARRIVAL: "Edge · late arrival",
  EDGE_VISIT_OVERRUN: "Edge · visit overrun",
  EDGE_OTP_OVERRIDE_REQUEST: "Edge · OTP override",

  // Roles / audiences
  CUSTOMER: "Customer",
  CAREGIVER: "Caregiver",
  CARE_MANAGER: "Care manager",
  ADMIN: "Admin",
  OPS_ADMIN: "Ops admin",

  // Severity
  INFO: "Info",
  WARN: "Warning",
  HIGH: "High",
  CRITICAL: "Critical",

  // Edge-event lifecycle
  OPEN: "Open",
  RESOLVED: "Resolved",
  HELD: "Held",
  CONSUMED: "Consumed",
  RELEASED: "Released",
  EXPIRED: "Expired",
  SENT: "Sent",

  // Account / KYC
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
  VERIFIED: "Verified",
  APPROVED: "Approved",
  IN_REVIEW: "Under review",
  SUBMITTED: "Submitted",
  DENIED: "Denied",
  COMPLETE: "Complete",

  // Service types
  FULL_TIME: "Full-time care",
  HOURLY: "Hourly visit",
  PART_TIME: "Part-time care",

  // Subscription / billing states
  AWAITING_TRIAL: "Deposit not started",
  DEPOSIT_DEFERRED: "Deposit pending",
  MONTH_2_DUE: "Monthly fee due",
  UNDER_INVESTIGATION: "Under investigation",
  ASSIGNED: "Assigned",
  ASSIGNED_TO_CARE_MANAGER: "Assigned to Care Manager",
  CUSTOMER_CONTACTED: "Customer contacted",
  ASSESSMENT_COMPLETED: "Assessment completed",
  SEARCHING_CAREGIVER: "Searching caregiver",
  BOOKING_CONFIRMED: "Booking confirmed",
  SERVICE_STARTED: "Service started",
  CARE_ATTENDANT: "Care Attendant",
  PHYSIOTHERAPIST: "Physiotherapist",
  TWELVE_HOUR: "12 Hours",
  TWENTY_FOUR_HOUR_LIVE_IN: "24 Hours Live-in",
  IMMEDIATELY: "Immediately",
  WITHIN_2_HOURS: "Within 2 hours",
  TODAY: "Today",
  TOMORROW: "Tomorrow",
  HOMEPAGE: "Homepage",
  DASHBOARD: "Dashboard",
  INVESTIGATION: "Under investigation",
  RESOLUTION: "Being resolved",
  CLOSURE: "Closed",

  // Complaint severity (HIGH already mapped above as "High")
  LOW: "Low priority",
  MEDIUM: "Medium priority",

  // Caregiver engagement lifecycle (PRD §14)
  SCHEDULED: "Scheduled",
  CAREGIVER_EN_ROUTE: "Caregiver en route",
  ARRIVED: "Arrived",
  AWAITING_START_OTP: "Awaiting start OTP",
  AWAITING_END_OTP: "Awaiting end OTP",
  SHIFT_COMPLETED: "Shift completed",
  EXTENDED: "Extended",
  CUSTOMER_ENDED_EARLY: "Customer ended early",
  CAREGIVER_ENDED_EARLY: "Caregiver ended early",
  REPLACEMENT_IN_PROGRESS: "Replacement in progress",
  REPLACEMENT_ACTIVE: "Replacement active",
  CANCELLED_BEFORE_START: "Cancelled before start",
  EMERGENCY_CLOSED: "Emergency closed",

  // Caregiver engagement
  ENGAGED: "Currently on a job",
  OPEN_TO_JOB: "Available for work",
});

function prettifyToken(token) {
  if (!token) return "";
  // Preserve common acronyms
  const upper = token.toUpperCase();
  if (["OTP", "ID", "KYC", "SLA", "ETA", "PAN", "DOB", "GST", "OPS"].includes(upper)) {
    return upper;
  }
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

/**
 * Convert a status / type value to a human label.
 * - Returns "" for empty input.
 * - Looks up `STATUS_OVERRIDES` first.
 * - Otherwise splits on `_` / `-` / spaces and sentence-cases each token,
 *   preserving common acronyms.
 */
export function formatStatus(value) {
  if (value === null || value === undefined) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  const upperKey = raw.toUpperCase().replace(/[\s-]+/g, "_");
  if (STATUS_OVERRIDES[upperKey]) return STATUS_OVERRIDES[upperKey];

  // Already-pretty values (lower/mixed case, contains spaces) — leave as-is.
  if (/[a-z]/.test(raw) && !/_/.test(raw)) return raw;

  const tokens = raw.split(/[_\-\s]+/).filter(Boolean);
  if (tokens.length === 0) return raw;
  return tokens.map(prettifyToken).join(" ");
}

/** Convenience: returns the lowercase CSS modifier slug for a status. */
export function statusSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default formatStatus;
